import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Link2,
  Link2Off,
  Undo2,
  Redo2,
  Quote,
  Pilcrow,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
} from "lucide-react";

/**
 * TipTap-based rich text editor.
 * Props:
 *   value: HTML string
 *   onChange: (html: string) => void
 *   placeholder: string
 *   testid: data-testid prefix (default "rte")
 */
export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Write a brief profile, achievements, contributions…",
  testid = "rte",
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        // Disable bundled Link & Underline — we configure them standalone below
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-[#234333] underline underline-offset-2",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[140px] px-3 py-2.5 focus:outline-none",
        "data-testid": `${testid}-content`,
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      // tiptap returns "<p></p>" for empty — normalize to ""
      const isEmpty = e.isEmpty;
      onChange?.(isEmpty ? "" : html);
    },
  });

  // Sync external value changes (e.g. when opening edit dialog with existing content)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current && !editor.isFocused) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href || "";
    // eslint-disable-next-line no-alert
    const url = window.prompt("Enter URL", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  };

  const ToolbarBtn = ({ active, onClick, icon: Icon, label, tid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={label}
      data-testid={`${testid}-${tid}`}
      className={`p-1.5 rounded-sm border text-[#1c1a17] transition-colors ${
        active
          ? "bg-[#234333] text-white border-[#234333]"
          : "bg-white border-[#e5e0d8] hover:bg-[#efece6]"
      }`}
    >
      <Icon size={14} strokeWidth={1.5} />
    </button>
  );

  return (
    <div
      className="border border-[#e5e0d8] rounded-sm bg-white overflow-hidden"
      data-testid={`${testid}-wrapper`}
    >
      <div
        className="flex flex-wrap gap-1 px-2 py-1.5 border-b border-[#e5e0d8] bg-[#f9f8f6]"
        data-testid={`${testid}-toolbar`}
      >
        <ToolbarBtn
          tid="bold"
          label="Bold"
          icon={Bold}
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarBtn
          tid="italic"
          label="Italic"
          icon={Italic}
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarBtn
          tid="underline"
          label="Underline"
          icon={UnderlineIcon}
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <span className="w-px bg-[#e5e0d8] mx-1" />
        <ToolbarBtn
          tid="h2"
          label="Heading 2"
          icon={Heading2}
          active={editor.isActive("heading", { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        />
        <ToolbarBtn
          tid="h3"
          label="Heading 3"
          icon={Heading3}
          active={editor.isActive("heading", { level: 3 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        />
        <ToolbarBtn
          tid="paragraph"
          label="Paragraph"
          icon={Pilcrow}
          active={editor.isActive("paragraph")}
          onClick={() => editor.chain().focus().setParagraph().run()}
        />
        <span className="w-px bg-[#e5e0d8] mx-1" />
        <ToolbarBtn
          tid="bullet"
          label="Bulleted list"
          icon={List}
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarBtn
          tid="ordered"
          label="Numbered list"
          icon={ListOrdered}
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolbarBtn
          tid="quote"
          label="Quote"
          icon={Quote}
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <span className="w-px bg-[#e5e0d8] mx-1" />
        <ToolbarBtn
          tid="align-left"
          label="Align left"
          icon={AlignLeft}
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        />
        <ToolbarBtn
          tid="align-center"
          label="Align center"
          icon={AlignCenter}
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        />
        <ToolbarBtn
          tid="align-right"
          label="Align right"
          icon={AlignRight}
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        />
        <ToolbarBtn
          tid="align-justify"
          label="Justify"
          icon={AlignJustify}
          active={editor.isActive({ textAlign: "justify" })}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        />
        <span className="w-px bg-[#e5e0d8] mx-1" />
        <ToolbarBtn
          tid="link"
          label="Link"
          icon={Link2}
          active={editor.isActive("link")}
          onClick={setLink}
        />
        <ToolbarBtn
          tid="unlink"
          label="Remove link"
          icon={Link2Off}
          active={false}
          onClick={() => editor.chain().focus().unsetLink().run()}
        />
        <span className="w-px bg-[#e5e0d8] mx-1" />
        <ToolbarBtn
          tid="undo"
          label="Undo"
          icon={Undo2}
          active={false}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolbarBtn
          tid="redo"
          label="Redo"
          icon={Redo2}
          active={false}
          onClick={() => editor.chain().focus().redo().run()}
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
